import { Col, notification } from 'antd'
import { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { RangeValue } from 'rc-picker/lib/interface'

import { useFilters, useGeneral, useUser } from '@contexts'
import { ImplementationTimeUtils } from '@utils'

import { DateFromTo } from '../StyledComponents'

interface DateFromToFilterProps {
  time: string
}
const TIME = {
  CURRENT: 'current',
  EXPIRED: 'expired',
}

const { companyStartOfToday, companyEndOfDay, companyStartOfDay } = ImplementationTimeUtils

const DateFromToFilter = ({ time }: DateFromToFilterProps) => {
  const { filters, setFilters, resetTriggered } = useFilters()
  const { t } = useTranslation()
  const { maxDaysRange, ticketToSearch, setTicketToSearch } = useGeneral()
  const { state } = useUser()
  const gmtZone = state?.company?.gmtZone ?? 0
  const companyToday = companyStartOfToday(gmtZone)
  const startDate =
    time === TIME.CURRENT
      ? companyToday
      : companyStartOfDay(companyToday.subtract(maxDaysRange + 1, 'd'), gmtZone)
  const endDate =
    time === TIME.EXPIRED
      ? companyEndOfDay(companyToday, gmtZone)
      : companyEndOfDay(companyToday.add(maxDaysRange, 'd'), gmtZone)
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(startDate)
  const [dateTo, setDateTo] = useState<Dayjs | null>(endDate)
  const [rangePickerOpen, setRangePickerOpen] = useState<boolean>(false)

  const calendarDateKey = (date: Dayjs) => date.format('YYYY-MM-DD')
  const todayKey = calendarDateKey(companyToday)

  useEffect(() => {
    if (!rangePickerOpen && dateFrom && !dateTo) {
      setDateTo(endDate)
      handleSetFilters([dateFrom, endDate])
    } else if (!rangePickerOpen && !dateFrom && dateTo) {
      setDateFrom(startDate)
      handleSetFilters([startDate, dateTo])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangePickerOpen])

  useEffect(() => {
    if (ticketToSearch) {
      setDateFrom(null)
      setDateTo(null)
    } else {
      setDateFrom(startDate)
      setDateTo(endDate)
      handleSetFilters([startDate, endDate])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketToSearch, resetTriggered])

  const onOpenChange = (open: boolean) => {
    if (open) {
      setRangePickerOpen(true)
    } else {
      setRangePickerOpen(false)
      if (!dateFrom && !dateTo) {
        setDateFrom(startDate)
        setDateTo(endDate)
        handleSetFilters([startDate, endDate])
      }
    }
  }

  const handleSetFilters = (values: RangeValue<Dayjs>) => {
    if (values && values[0] !== null && values[1] !== null) {
      setFilters({
        ...filters,
        dateFrom: companyStartOfDay(values[0], gmtZone).toDate(),
        dateTo: companyEndOfDay(values[1], gmtZone).toDate(),
      })
    } else {
      notification.error({ message: t('globals.generics.apply.error'), duration: 4 })
    }
  }
  const onChange = (values: RangeValue<Dayjs>) => {
    if (values) {
      const from = values[0] ? companyStartOfDay(values[0], gmtZone) : null
      const to = values[1] ? companyEndOfDay(values[1], gmtZone) : null
      setDateFrom(from)
      setDateTo(to)
      if (from !== null && to !== null) {
        handleSetFilters([from, to])
      }
    }
  }
  const onCalendarChange = (values: RangeValue<Dayjs>): void => {
    setDateFrom(values?.[0] ? companyStartOfDay(values[0], gmtZone) : null)
    setDateTo(values?.[1] ? companyEndOfDay(values[1], gmtZone) : null)
  }
  const disableDate = (current: Dayjs) => {
    const currentKey = calendarDateKey(current)

    if (time === TIME.CURRENT) {
      return currentKey < todayKey
    }
    return currentKey > todayKey
  }
  return (
    <Col className="from-to-handler-container">
      <DateFromTo
        disabledDate={disableDate}
        format="DD/MM/YYYY"
        value={[dateFrom, dateTo]}
        onCalendarChange={onCalendarChange}
        onChange={onChange}
        onOpenChange={onOpenChange}
        allowClear={false}
        onFocus={() => setTicketToSearch(undefined)}
        popupClassName="date-from-to-picker-dropdown"
        inputReadOnly={false}
      />
    </Col>
  )
}

export { DateFromToFilter, TIME }
